import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import { api } from './api';

/**
 * Passkeys — Face ID sign-in.
 *
 * The browser does the biometric prompt and the signing; this file only carries
 * the two halves of each exchange between the phone and the server. Nothing
 * secret passes through it: what comes back from the authenticator is a
 * signature over a challenge the server issued.
 */

export interface PasskeySummary {
  id: string;
  deviceName: string | null;
  backedUp: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export function supported(): boolean {
  return browserSupportsWebAuthn();
}

/**
 * Whether this device can create a passkey backed by its own biometrics —
 * Face ID, Touch ID, Windows Hello — rather than a roaming security key.
 * Offering "Sign in with Face ID" on hardware that has none is a broken promise.
 */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!supported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** True once the user has a passkey on this browser to sign in with. */
export async function conditionalUiAvailable(): Promise<boolean> {
  if (!supported()) return false;
  try {
    return await PublicKeyCredential.isConditionalMediationAvailable?.() ?? false;
  } catch {
    return false;
  }
}

export async function register(): Promise<{ deviceName: string }> {
  const options = await api.post<Record<string, unknown>>('/auth/passkey/register-options');
  const response = await startRegistration({ optionsJSON: options as never });
  return api.post<{ deviceName: string }>('/auth/passkey/register-verify', { response });
}

export interface PasskeyLoginResult {
  accessToken: string;
  refreshToken: string;
  user: { id: string; email: string; currency: string; locale: string; onboardingComplete: boolean };
}

/**
 * Sign in with a passkey. `conditional` drives the autofill-style prompt that
 * appears as soon as the sign-in form is focused, rather than on a tap.
 */
export async function login(conditional = false): Promise<PasskeyLoginResult> {
  const options = await api.post<Record<string, unknown>>('/auth/passkey/login-options');
  const response = await startAuthentication({ optionsJSON: options as never, useBrowserAutofill: conditional });
  return api.post<PasskeyLoginResult>('/auth/passkey/login-verify', { response });
}

export async function list(): Promise<PasskeySummary[]> {
  return (await api.get<{ passkeys: PasskeySummary[] }>('/auth/passkeys')).passkeys;
}

export async function remove(id: string): Promise<void> {
  await api.del(`/auth/passkeys/${id}`);
}

/**
 * Distinguishes "the user changed their mind" from a real failure.
 *
 * Cancelling Face ID throws, and reporting that as an error reads as though the
 * app is broken when nothing went wrong at all.
 */
export function wasCancelled(err: unknown): boolean {
  const name = (err as { name?: string })?.name;
  return name === 'NotAllowedError' || name === 'AbortError';
}
